import { getAccountStorage } from '../../../backend/features/accounts/utils/getAccountStorage';
import { getOrgPermissions } from '../../../backend/features/organizations/utils/getOrgPermissions';
import { isOrganizationVisible } from '../../../backend/features/organizations/utils/isOrganizationVisible';
import { collectOrgIdsForAnyOrgPermission, requireAnySystemPermission } from '../../../backend/features/permissions/shared';
import { raw, sql } from '@juvare/platform-app/Integration';
export class OrgTreeHelper {
    constructor(context) {
        this.expandedUniqueIds = new Set();
        const account = getAccountStorage(context).get(context.userState.id);
        this.context = context;
        this.orgNetwork = context.appState.organizations;
        this.totalOrganizations = Object.keys(this.orgNetwork).length;
        this.canAccessAll = requireAnySystemPermission(context.userState, 'view-invisible-organizations', 'all-organizations-admin');
        this.accountMembership = new Set(account.columns.orgMembership);
        this.accountAssociation = new Set(account.columns.orgAssociation);
        this.accountManageableOrganizations = collectOrgIdsForAnyOrgPermission(context.userState, 'manage-members', 'manage-organization');
    }
    getOrganizationsTree({ filterData, staticFilters, parentId, parentPath, maxDepth, maxExpandedIds, initialOrganizationIds, }) {
        var _a;
        const initialOrganizationsExist = initialOrganizationIds && initialOrganizationIds.length > 0;
        if (filterData && Object.keys(filterData).length > 0) {
            const ids = [];
            if (filterData) {
                ids.push(...this.filterOrganizations(filterData));
            }
            if (ids.length > 0) {
                const expandedPaths = this.getExpandedIdPaths(ids);
                return {
                    organizations: this.visitRecursiveByExpandedIds({
                        vertices: this.orgNetwork['ROOT'].adjacencyList,
                        expandedIds: new Set(ids),
                        expandedIdPaths: expandedPaths.map((item) => item.path),
                        parent: undefined,
                        includeVisible: !!(staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.visible),
                        orgPermissions: getOrgPermissions(filterData, staticFilters),
                        includeByMembership: !!(filterData === null || filterData === void 0 ? void 0 : filterData.permission) || (staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.permission) === 'member',
                        isSearch: !!(filterData === null || filterData === void 0 ? void 0 : filterData.search),
                    }),
                    highlightedIds: Array.from(this.expandedUniqueIds).slice(0, maxExpandedIds),
                };
            }
            else {
                return {
                    organizations: [],
                    highlightedIds: [],
                };
            }
        }
        else {
            const ids = [];
            if ((staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.permission) === 'member') {
                ids.push(...this.accountAssociation);
            }
            if (staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.visible) {
                ids.push(...this.filterOrganizations(undefined));
            }
            if (!!((_a = staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.orgPermissions) === null || _a === void 0 ? void 0 : _a.length)) {
                ids.push(...collectOrgIdsForAnyOrgPermission(this.context.userState, ...staticFilters.orgPermissions));
            }
            if (initialOrganizationsExist) {
                ids.push(...initialOrganizationIds);
            }
            const expandedMembershipPaths = this.getExpandedIdPaths(ids);
            return {
                organizations: this.visitRecursive({
                    vertices: this.orgNetwork[parentId !== null && parentId !== void 0 ? parentId : 'ROOT'].adjacencyList,
                    membershipAndNetworkPaths: expandedMembershipPaths.map((item) => item.path),
                    initialIds: initialOrganizationIds,
                    staticFilters: staticFilters,
                    maxDepth: maxDepth,
                    depth: 0,
                    parent: parentPath ? { id: parentPath, children: [], path: '', name: '', visible: true } : undefined,
                }),
                highlightedIds: [],
            };
        }
    }
    visitRecursive({ vertices, parent, staticFilters, membershipAndNetworkPaths, initialIds, maxDepth, depth = 0, }) {
        const userOrganizations = [];
        if (depth === maxDepth) {
            return userOrganizations;
        }
        for (const id of vertices) {
            const userOrganization = this.createOrganizationItem(id, parent, staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.orgPermissions, staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.visible);
            userOrganization.children = this.visitRecursive({
                vertices: this.orgNetwork[id].adjacencyList,
                membershipAndNetworkPaths: membershipAndNetworkPaths,
                initialIds: initialIds,
                staticFilters: staticFilters,
                maxDepth: maxDepth,
                depth: depth + 1,
                parent: userOrganization,
            });
            const isInPath = maxDepth > 0 && membershipAndNetworkPaths.find((path) => `${path}/`.startsWith(`${this.orgNetwork[id].path}/`));
            const isOrganizationVisible = this.isOrganizationVisible(id, (staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.permission) === 'member', staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.orgPermissions, staticFilters === null || staticFilters === void 0 ? void 0 : staticFilters.visible, undefined, !!(initialIds === null || initialIds === void 0 ? void 0 : initialIds.length));
            if (isOrganizationVisible || userOrganization.children.length > 0 || isInPath) {
                //Initial ids can fall under direct or networked parents.
                //In this case, direct parents should be disabled because only selected orgs should be interactable
                //Otherwise enable initial ids org
                if (initialIds && !initialIds.includes(id)) {
                    userOrganization.disabled = true;
                }
                else if (initialIds && initialIds.includes(id)) {
                    userOrganization.disabled = false;
                }
                userOrganizations.push(userOrganization);
            }
        }
        return userOrganizations;
    }
    visitRecursiveByExpandedIds({ vertices, expandedIds, expandedIdPaths, parent, includeVisible, orgPermissions, includeByMembership, isSearch, }) {
        if (expandedIds.size === 0) {
            return [];
        }
        const userOrganizations = [];
        for (const id of vertices) {
            const userOrganization = this.createOrganizationItem(id, parent, orgPermissions, includeVisible, isSearch);
            if (expandedIds.has(id)) {
                this.expandedUniqueIds.add(userOrganization.id);
            }
            // end recursion early if top level orgs are not under found paths
            // for large number of expanded ids full recursion gets faster
            if (expandedIds.size < 100 && !expandedIdPaths.find((path) => path.startsWith(this.orgNetwork[id].path))) {
                continue;
            }
            userOrganization.children = this.visitRecursiveByExpandedIds({
                vertices: this.orgNetwork[id].adjacencyList,
                expandedIds: expandedIds,
                expandedIdPaths: expandedIdPaths,
                parent: userOrganization,
                includeVisible: includeVisible,
                orgPermissions: orgPermissions,
                includeByMembership: includeByMembership,
                isSearch,
            });
            if (this.isOrganizationVisible(id, includeByMembership, orgPermissions, includeVisible, isSearch)) {
                // when has access add only if org needs to be expanded or parent has expanded children
                if (expandedIds.has(id) || userOrganization.children.length > 0 || includeVisible) {
                    userOrganizations.push(userOrganization);
                }
            }
            else if (userOrganization.children.length > 0) {
                // when has no access but is parent of found children disable it and add to tree
                userOrganization.disabled = true;
                userOrganizations.push(userOrganization);
            }
        }
        return userOrganizations;
    }
    filterOrganizations(filterData) {
        const clauses = [];
        if (filterData === null || filterData === void 0 ? void 0 : filterData.search) {
            clauses.push(sql `lower(c.name) LIKE '%' || lower(${filterData.search}) || '%'`);
        }
        if ((filterData === null || filterData === void 0 ? void 0 : filterData.permission) === 'manage') {
            const ids = collectOrgIdsForAnyOrgPermission(this.context.userState, 'view-and-message-members', 'create-notifications', 'manage-members', 'manage-organization');
            if (ids.length > 0) {
                clauses.push(sql `c._id IN (${ids})`);
            }
        }
        if ((filterData === null || filterData === void 0 ? void 0 : filterData.permission) === 'member') {
            if (Array.from(this.accountAssociation).length > 0) {
                clauses.push(sql `c._id IN (${Array.from(this.accountAssociation)})`);
            }
        }
        if (filterData === null || filterData === void 0 ? void 0 : filterData.pending) {
            clauses.push(sql `c._id = pmc.org_id and pmc.pending_member_count > 0`);
            if (!this.canAccessAll && this.accountManageableOrganizations.length) {
                clauses.push(sql `c._id = ANY(ARRAY[${this.accountManageableOrganizations}]::uuid[])`);
            }
        }
        let whereClause = sql ``;
        for (let i = 0; i < clauses.length; i++) {
            if (i === 0) {
                whereClause = sql `${whereClause} ${clauses[i]}`;
            }
            else {
                if (clauses[i].query.includes('c.visible IS TRUE')) {
                    whereClause = sql `${whereClause} OR ${clauses[i]}`;
                }
                else {
                    whereClause = sql `${whereClause} AND ${clauses[i]}`;
                }
            }
        }
        if (clauses.length === 0) {
            return [];
        }
        const query = sql `
            WITH pending_members AS (
                SELECT unnest(ac.org_status) AS org_status, ac._id AS account_id
                FROM account_cache ac
                WHERE ac.system_status = 'active' AND ac.org_status_set && '{"pending"}'
                ),
            pending_member_count AS (
                 SELECT replace(pm.org_status, '_pending', '')::uuid AS org_id, COUNT(pm.account_id) AS pending_member_count
                 FROM pending_members pm
                 WHERE pm.org_status like '%_pending'
                 GROUP BY pm.org_status
             )
            SELECT _id AS id, visible FROM organization_cache c ${raw((filterData === null || filterData === void 0 ? void 0 : filterData.pending) ? ', pending_member_count pmc' : '')} WHERE ${whereClause}
`;
        return this.context.dbClient
            .queryList(query)
            .map((item) => item.id);
    }
    createOrganizationItem(orgId, parent, orgPermissions, includeVisible, isSearch) {
        var _a;
        const org = {
            id: `${orgId}${!!parent ? `_${parent.id}` : ''}`,
            name: this.orgNetwork[orgId].name,
            visible: this.orgNetwork[orgId].visible,
            path: this.orgNetwork[orgId].path,
            maxNumberOfMembershipsPerOrgTree: this.orgNetwork[orgId].maxNumberOfMembershipsPerOrgTree,
            disabled: true,
            children: [],
        };
        if (this.canAccessAll) {
            org.disabled = false;
        }
        else if ((_a = this.context.userState.orgPermissions[orgId]) === null || _a === void 0 ? void 0 : _a.some((permission) => orgPermissions === null || orgPermissions === void 0 ? void 0 : orgPermissions.includes(permission))) {
            org.disabled = false;
        }
        else if (org.visible && includeVisible) {
            org.disabled = false;
        }
        else if (this.accountMembership.has(orgId) && !(orgPermissions === null || orgPermissions === void 0 ? void 0 : orgPermissions.length)) {
            org.disabled = false;
        }
        else if (isSearch && (!!this.context.userState.orgPermissions[orgId] || org.visible)) {
            org.disabled = false;
        }
        return org;
    }
    // Used in select all to fetch all children in the branch
    getAllChildrenIds({ parentId, includeByMembership, includeByPermissions, includeVisible, }) {
        const result = [];
        const orgId = parentId.split('_')[0];
        const getChildrenIdsRecursive = (orgTreeId, orgId) => {
            const node = this.orgNetwork[orgId];
            if (this.isOrganizationVisible(orgId, includeByMembership, includeByPermissions, includeVisible)) {
                result.push(orgTreeId);
                if (node && !!node.adjacencyList.length) {
                    for (const childId of node.adjacencyList) {
                        getChildrenIdsRecursive(`${childId}_${orgTreeId}`, childId);
                    }
                }
            }
        };
        getChildrenIdsRecursive(parentId, orgId);
        return result;
    }
    isOrganizationVisible(orgId, includeByMembership, includeByPermissions, includeVisible, isSearch, skipCanAccessAll) {
        return isOrganizationVisible({
            orgId,
            orgNetwork: this.orgNetwork,
            userState: this.context.userState,
            includeByMembership,
            includeByPermissions,
            includeVisible,
            isSearch,
            skipCanAccessAll,
        });
    }
    getExpandedIdPaths(expandedIds) {
        const query = sql `
                WITH RECURSIVE
                        flat_orgs AS (
                            SELECT _id, _parent_id, name
                                FROM organization_cache
                                UNION ALL
                                SELECT _id, unnest(parent_ids) AS _parent_id, name
                                FROM organization_cache
                              ),
                              path_cte AS (
                                  SELECT
                                      _id,
                                      _parent_id,
                                      name,
                                      _id::text || COALESCE('_' || _parent_id::text, '') AS cumulative_id,
                                      REPLACE(name, '/', '\\/') AS path
                                  FROM flat_orgs
                                  WHERE _id = ANY(ARRAY[${expandedIds}]::uuid[])
                                  UNION ALL
                                  SELECT
                                      t._id,
                                      t._parent_id,
                                      t.name,
                                      cte.cumulative_id || COALESCE('_' || t._parent_id::text, ''),
                                      REPLACE(t.name, '/', '\\/') || '/' || cte.path
                                  FROM flat_orgs t
                                  JOIN path_cte cte ON cte._parent_id = t._id
                                )
                          SELECT path, cumulative_id FROM path_cte
                          WHERE _parent_id IS NULL;`;
        return this.context.dbClient
            .queryList(query)
            .map((item) => ({ path: item.path, id: item.cumulative_id }));
    }
}
